import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BASE_URL = "https://nissi-india.com"; // change if needed

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function toNum(v) {
  // supports numbers and "26,132.10"
  const raw = (v ?? "").toString().replace(/,/g, "").trim();
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

const BankPaymentReportPrint = () => {
  const q = useQuery();

  const finYearId = q.get("finYearId") || localStorage.getItem("finYearId") || "";
  const monthName = q.get("monthName") || "Select";
  const year = q.get("year") || "Select"; // not shown in screenshot title, but available
  const branchIds = q.get("branchIds") || ""; // csv: "8,10"

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Optional company info (same pattern you used in PF print)
  const companyName = localStorage.getItem("companyName") || "Nissi Infotech";
  const companyLine1 = localStorage.getItem("companyLine1") || "Nissi";
  const companyLine2 = localStorage.getItem("companyLine2") || "CIVIL CONSTRUCTION";
  const companyAddr =
    localStorage.getItem("companyAddr") ||
    "3/42-12, Nagappa colony, kavundampalayam - 641030. Tamil Nadu.";
  const companyPhone = localStorage.getItem("companyPhone") || "Phone No : 9003744644";

  const fetchPrintData = async () => {
    if (!String(finYearId).trim()) return;

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/bank-payment-report/print-data`, {
        params: {
          finYearId: Number(finYearId),
          monthName,
          year,
          branchIds, // csv (Node supports csv or array)
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalNet = useMemo(() => {
    return rows.reduce((acc, r) => acc + toNum(r?.fld_Netpay), 0);
  }, [rows]);

  const handlePrint = () => window.print();

  return (
    <div className="print-root">
      <style>{`
        .print-root{
          background:#fff;
          color:#000;
          padding: 16px;
          font-family: "Times New Roman", Times, serif;
        }
        .page {
          border: 2px solid #000;
          padding: 14px;
          max-width: 1050px;
          margin: 0 auto;
        }
        .headerBox{
          border:1px solid #000;
          padding: 10px;
          display:flex;
          gap:14px;
          align-items:center;
          justify-content:space-between;
        }
        .logoBox{
          width:140px;
          height:75px;
          border:1px solid #000;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:700;
          font-size:12px;
        }
        .companyText{
          flex:1;
          text-align:center;
          line-height:1.15;
        }
        .companyText .name{ font-weight:700; font-size:20px; }
        .companyText .sub{ font-weight:700; font-size:13px; }
        .companyText .addr{ font-size:12px; margin-top:2px; }

        .titleWrap{
          margin-top: 10px;
          border: 1px solid #000;
          border-left: none;
          border-right: none;
          padding: 10px 0;
          text-align:center;
          font-weight:700;
          font-size:18px;
          letter-spacing: 0.5px;
        }

        table{
          width:100%;
          border-collapse:collapse;
          margin-top: 12px;
          font-size:13px;
        }
        th, td{
          border:1px solid #000;
          padding:8px 8px;
          vertical-align:middle;
        }
        th{
          font-weight:700;
          text-align:center;
        }
        td{ text-align:center; }
        .left{ text-align:left; }
        .right{ text-align:right; }
        .totRow td{ font-weight:700; }

        .actions{
          max-width:1050px;
          margin: 12px auto 0;
          display:flex;
          justify-content:flex-end;
          gap:10px;
        }
        .btn{
          border:1px solid #000;
          background:#fff;
          padding:8px 14px;
          cursor:pointer;
          font-weight:700;
        }

        @media print{
          .actions{ display:none; }
          .print-root{ padding:0; }
          .page{ border:none; padding:0; max-width:unset; margin:0; }
          @page { margin: 10mm; }
        }
      `}</style>

      {/* Buttons (hidden in print) */}
      <div className="actions">
        <button className="btn" onClick={handlePrint}>Print</button>
      </div>

      <div className="page">
        {/* Header */}
        <div className="headerBox">
          <div className="logoBox">LOGO</div>

          <div className="companyText">
            <div className="name">{companyName}</div>
            <div className="sub">{companyLine1}</div>
            <div className="sub">{companyLine2}</div>
            <div className="addr">{companyAddr}</div>
            <div className="addr">{companyPhone}</div>
          </div>

          <div style={{ width: 140 }} />
        </div>

        {/* Title */}
        <div className="titleWrap">
          BANK LIST FOR THE MONTH OF {String(monthName || "").toUpperCase()}
        </div>

        {/* Table */}
        <table>
          <thead>
            <tr>
              <th style={{ width: "7%" }}>S.No</th>
              <th style={{ width: "18%" }}>Account No.</th>
              <th style={{ width: "14%" }}>Employee Code</th>
              <th style={{ width: "22%" }}>Employee Name</th>
              <th style={{ width: "16%" }}>Net Salary</th>
              <th style={{ width: "23%" }}>Location</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="left">Loading...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6}>No Records Found</td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{r.fld_accountno || ""}</td>
                  <td>{r.fld_Employeecode || ""}</td>
                  <td className="left">{r.fld_Employee || ""}</td>
                  <td className="right">{toNum(r.fld_Netpay).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="left">{r.fld_BranchName || ""}</td>
                </tr>
              ))
            )}

            {/* Total row */}
            {!loading && rows.length > 0 && (
              <tr className="totRow">
                <td colSpan={3} />
                <td>Total</td>
                <td className="right">
                  {totalNet.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BankPaymentReportPrint;
